import { motion } from "framer-motion";
import { MapPin, Target, Shield, Sparkles } from "lucide-react";
import type { TrustProof, CoverageState, ConfidenceLevel } from "@/lib/decisionEngine";
import type { ParsedQuery } from "@/lib/queryParser";

interface Props {
  parsed: ParsedQuery;
  proof: TrustProof;
  coverage: CoverageState;
  confidence: ConfidenceLevel;
}

const confidenceLabel: Record<ConfidenceLevel, { text: string; className: string }> = {
  high: { text: "High confidence", className: "text-score-high" },
  medium: { text: "Moderate confidence", className: "text-score-mid" },
  low: { text: "Limited confidence", className: "text-muted-foreground" },
  insufficient: { text: "Insufficient data", className: "text-score-low" },
};

const FLAG_EMOJI: Record<string, string> = {
  US: "\u{1F1FA}\u{1F1F8}", GB: "\u{1F1EC}\u{1F1E7}", CA: "\u{1F1E8}\u{1F1E6}", AU: "\u{1F1E6}\u{1F1FA}", DE: "\u{1F1E9}\u{1F1EA}", AE: "\u{1F1E6}\u{1F1EA}", JP: "\u{1F1EF}\u{1F1F5}", IN: "\u{1F1EE}\u{1F1F3}",
  FR: "\u{1F1EB}\u{1F1F7}", NL: "\u{1F1F3}\u{1F1F1}", SE: "\u{1F1F8}\u{1F1EA}", ES: "\u{1F1EA}\u{1F1F8}", IT: "\u{1F1EE}\u{1F1F9}", BR: "\u{1F1E7}\u{1F1F7}", MX: "\u{1F1F2}\u{1F1FD}", KR: "\u{1F1F0}\u{1F1F7}", NZ: "\u{1F1F3}\u{1F1FF}", SG: "\u{1F1F8}\u{1F1EC}",
};

export default function DecisionSummaryBar({ parsed, proof, coverage, confidence }: Props) {
  const flag = parsed.region ? FLAG_EMOJI[parsed.region] || "" : "\u{1F30D}";
  const country = parsed.country || "Global";
  const budget = parsed.budget ? `Under ${parsed.budget.currency === "USD" ? "$" : ""}${parsed.budget.max.toLocaleString()}` : null;
  const priority = parsed.priorities[0] || "Overall quality";
  const conf = confidenceLabel[confidence];

  const isAIPowered = proof.retrievalModes?.includes("ai-powered");

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: 0.05 }}
      className="w-full max-w-5xl mx-auto mb-6"
    >
      <div className="rounded-xl border border-border/60 bg-card/60 px-4 py-3 border-l-2 border-l-primary">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 text-sm font-body">
          <span className="flex items-center gap-1.5 text-foreground">
            <MapPin className="w-3 h-3 text-muted-foreground" />
            {flag} {country}
          </span>
          {budget && (
            <>
              <span className="text-muted-foreground/40">/</span>
              <span className="text-foreground">{budget}</span>
            </>
          )}
          <span className="text-muted-foreground/40">/</span>
          <span className="flex items-center gap-1 text-foreground">
            <Target className="w-3 h-3 text-muted-foreground" />
            {priority}
          </span>
          {parsed.localPreference && (
            <>
              <span className="text-muted-foreground/40">/</span>
              <span className="text-muted-foreground capitalize">{parsed.localPreference}</span>
            </>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 mt-2 text-xs font-body text-muted-foreground">
          {proof.offersChecked > 0 ? (
            <>
              <span className="flex items-center gap-1.5">
                Analyzed <span className="text-foreground font-medium">{proof.offersChecked}</span> offers across <span className="text-foreground font-medium">{proof.sourcesChecked}</span> sources
              </span>
              {proof.removed > 0 && (
                <span className="flex items-center gap-1.5">
                  · Filtered <span className="text-foreground font-medium">{proof.removed}</span> weak options
                </span>
              )}
            </>
          ) : (
            <span className="flex items-center gap-1.5">
              <Sparkles className="w-3 h-3 text-score-mid" />
              AI-estimated only — no live retailer data verified for this query
            </span>
          )}
          <span className="flex items-center gap-1.5">
            <Shield className={`w-3 h-3 ${conf.className}`} />
            <span className={conf.className}>{conf.text}</span>
          </span>
        </div>
      </div>
    </motion.div>
  );
}
